(() => {
  const STORAGE_KEY = 'theme:wishlist-products';
  const MAX_ITEMS = 50;

  const strings = () => (window.themeVariables && window.themeVariables.strings) || {};

  function readWishlist() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function writeWishlist(items) {
    const next = items.slice(0, MAX_ITEMS);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      /* private mode / quota */
    }
    document.dispatchEvent(
      new CustomEvent('wishlist:change', {
        bubbles: true,
        detail: { items: next, count: next.length }
      })
    );
  }

  function normalizeId(id) {
    return String(id == null ? '' : id);
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  const WishlistStore = {
    getAll() {
      return readWishlist();
    },

    getCount() {
      return readWishlist().length;
    },

    has(productId) {
      const id = normalizeId(productId);
      if (!id) return false;
      return readWishlist().some((item) => normalizeId(item.id) === id);
    },

    add(item) {
      if (!item || item.id == null || item.id === '') return readWishlist();

      const items = readWishlist().filter((entry) => normalizeId(entry.id) !== normalizeId(item.id));
      items.unshift({
        id: String(item.id),
        handle: item.handle || '',
        url: item.url || '',
        title: item.title || '',
        image: item.image || '',
        price: item.price || '',
        compare_at_price: item.compare_at_price || '',
        variant_id: item.variant_id ? String(item.variant_id) : '',
        variant_title: item.variant_title || ''
      });
      writeWishlist(items);
      return items;
    },

    remove(productId) {
      const id = normalizeId(productId);
      const items = readWishlist().filter((entry) => normalizeId(entry.id) !== id);
      writeWishlist(items);
      return items;
    },

    toggle(item) {
      if (this.has(item.id)) {
        return { items: this.remove(item.id), added: false };
      }
      return { items: this.add(item), added: true };
    },

    clear() {
      writeWishlist([]);
    }
  };

  window.WishlistStore = WishlistStore;

  if (!window.customElements.get('wishlist-count')) {
    class WishlistCount extends HTMLElement {
      connectedCallback() {
        this._onChange = (event) => this.render(event.detail?.count ?? WishlistStore.getCount());
        this.render(WishlistStore.getCount());
        document.addEventListener('wishlist:change', this._onChange);
        window.addEventListener('pageshow', this._onChange);
      }

      disconnectedCallback() {
        document.removeEventListener('wishlist:change', this._onChange);
        window.removeEventListener('pageshow', this._onChange);
      }

      render(count) {
        const value = Number(count) || 0;
        this.textContent = String(value);
        this.hidden = value < 1;
      }
    }

    window.customElements.define('wishlist-count', WishlistCount);
  }

  if (!window.customElements.get('wishlist-dot')) {
    class WishlistDot extends HTMLElement {
      connectedCallback() {
        this._onChange = (event) => this.update(event.detail?.count ?? WishlistStore.getCount());
        this.update(WishlistStore.getCount());
        document.addEventListener('wishlist:change', this._onChange);
        window.addEventListener('pageshow', this._onChange);
      }

      disconnectedCallback() {
        document.removeEventListener('wishlist:change', this._onChange);
        window.removeEventListener('pageshow', this._onChange);
      }

      update(count) {
        this.classList.toggle('is-visible', (Number(count) || 0) > 0);
      }
    }

    window.customElements.define('wishlist-dot', WishlistDot);
  }

  if (!window.customElements.get('wishlist-button')) {
    class WishlistButton extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button) return;

        this._onChange = () => this.sync();
        this._onClick = this.onClick.bind(this);

        this.sync();
        this.button.addEventListener('click', this._onClick);
        document.addEventListener('wishlist:change', this._onChange);
        window.addEventListener('pageshow', this._onChange);
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this._onClick);
        document.removeEventListener('wishlist:change', this._onChange);
        window.removeEventListener('pageshow', this._onChange);
      }

      get productData() {
        return {
          id: this.getAttribute('data-product-id'),
          handle: this.getAttribute('data-product-handle') || '',
          url: this.getAttribute('data-product-url') || '',
          title: this.getAttribute('data-product-title') || '',
          image: this.getAttribute('data-product-image') || '',
          price: this.getAttribute('data-product-price') || '',
          compare_at_price: this.getAttribute('data-product-compare-at-price') || '',
          variant_id: this.getAttribute('data-variant-id'),
          variant_title: this.getAttribute('data-variant-title') || ''
        };
      }

      sync() {
        const active = WishlistStore.has(this.getAttribute('data-product-id'));
        this.classList.toggle('is-active', active);
        this.button?.setAttribute('aria-pressed', active ? 'true' : 'false');

        const label = active
          ? this.getAttribute('data-label-remove') || strings().removeFromWishlist || 'Remove from wishlist'
          : this.getAttribute('data-label-add') || strings().addToWishlist || 'Add to wishlist';

        const srOnly = this.button?.querySelector('.sr-only');
        if (srOnly) srOnly.textContent = label;
        this.button?.setAttribute('aria-label', label);
      }

      onClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const { added } = WishlistStore.toggle(this.productData);
        this.sync();

        if (added) {
          const drawer = document.getElementById('wishlist-drawer');
          if (drawer && typeof drawer.show === 'function') {
            drawer.show();
          }
        }
      }
    }

    window.customElements.define('wishlist-button', WishlistButton);
  }

  if (!window.customElements.get('wishlist-panel')) {
    class WishlistPanel extends HTMLElement {
      connectedCallback() {
        this._onChange = (event) => this.render(event.detail?.items);
        this._onClick = this.onClick.bind(this);
        this._onShow = () => this.render();

        this.render();
        document.addEventListener('wishlist:change', this._onChange);
        window.addEventListener('pageshow', this._onChange);
        this.addEventListener('click', this._onClick);

        const drawer = this.closest('#wishlist-drawer, x-drawer, .wishlist-drawer');
        drawer?.addEventListener('dialog:before-show', this._onShow);
        this._drawer = drawer;
      }

      disconnectedCallback() {
        document.removeEventListener('wishlist:change', this._onChange);
        window.removeEventListener('pageshow', this._onChange);
        this.removeEventListener('click', this._onClick);
        this._drawer?.removeEventListener('dialog:before-show', this._onShow);
      }

      onClick(event) {
        const quickViewBtn = event.target.closest('[data-wishlist-quick-view]');
        if (quickViewBtn) {
          this._drawer?.hide?.();
          return;
        }

        const removeBtn = event.target.closest('[data-wishlist-remove]');
        if (removeBtn) {
          event.preventDefault();
          WishlistStore.remove(removeBtn.getAttribute('data-wishlist-remove'));
          return;
        }

        const clearBtn = event.target.closest('[data-wishlist-clear]');
        if (clearBtn) {
          event.preventDefault();
          WishlistStore.clear();
        }
      }

      formatItem(item) {
        const removeLabel = escapeHtml(strings().removeFromWishlist || 'Remove');
        const quickViewLabel = escapeHtml(strings().quickView || strings().viewProduct || 'Quick view');
        const title = escapeHtml(item.title);
        const url = escapeHtml(item.url);
        const price = escapeHtml(item.price);
        const imageUrl = escapeHtml(item.image);
        const variantTitle = escapeHtml(item.variant_title);
        const compareAt = escapeHtml(item.compare_at_price);
        const id = escapeHtml(item.id);
        const handle = escapeHtml(item.handle || '');
        const variantId = escapeHtml(item.variant_id || '');
        const modalId = `wishlist-quick-buy-${id}`;
        const handleAttr = variantId ? `${handle}?variant=${variantId}` : handle;

        const image = item.image
          ? `<a href="${url}" class="wishlist-drawer__media" data-instant>
              <img src="${imageUrl}" alt="" width="120" height="120" loading="lazy" decoding="async">
            </a>`
          : `<div class="wishlist-drawer__media wishlist-drawer__media--placeholder"></div>`;

        const compare =
          item.compare_at_price && item.compare_at_price !== item.price
            ? `<span class="wishlist-drawer__compare">${compareAt}</span>`
            : '';

        const variant =
          item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="wishlist-drawer__variant text-sm text-subdued">${variantTitle}</p>`
            : '';

        const quickView = handle
          ? `<button type="button" class="wishlist-drawer__quick-view" aria-controls="${modalId}" data-wishlist-quick-view>${quickViewLabel}</button>
             <quick-buy-modal id="${modalId}" handle="${handleAttr}" class="quick-buy-modal modal modal--lg color-scheme color-scheme--dialog"></quick-buy-modal>`
          : `<a href="${url}" class="wishlist-drawer__view link" data-instant>${escapeHtml(strings().viewProduct || 'View product')}</a>`;

        return `
          <article class="wishlist-drawer__item" data-product-id="${id}">
            ${image}
            <div class="wishlist-drawer__info">
              <a href="${url}" class="wishlist-drawer__title" data-instant>${title}</a>
              ${variant}
              <div class="wishlist-drawer__price">
                <span>${price}</span>
                ${compare}
              </div>
              ${quickView}
            </div>
            <button type="button" class="wishlist-drawer__remove" data-wishlist-remove="${id}" aria-label="${removeLabel}">
              <svg aria-hidden="true" fill="none" focusable="false" width="16" viewBox="0 0 14 14">
                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </article>
        `;
      }

      render(itemsFromEvent) {
        const items = Array.isArray(itemsFromEvent) ? itemsFromEvent : WishlistStore.getAll();
        const isEmpty = items.length === 0;
        const drawer = this._drawer || this.closest('#wishlist-drawer, x-drawer, .wishlist-drawer');
        const emptyState = this.querySelector('[data-wishlist-empty]');
        const filledState = this.querySelector('[data-wishlist-filled]');
        const itemsRoot = this.querySelector('[data-wishlist-items]');

        drawer?.classList.toggle('drawer--center-body', isEmpty);

        if (emptyState) emptyState.hidden = !isEmpty;
        if (filledState) filledState.hidden = isEmpty;
        if (itemsRoot) itemsRoot.innerHTML = isEmpty ? '' : items.map((item) => this.formatItem(item)).join('');
      }
    }

    window.customElements.define('wishlist-panel', WishlistPanel);
  }
})();
